// Display · Skeleton
//
// Single `<span>` block with a pulse animation, sized via `width` + `height`.
// Uses MOTION.skeletonPulse + TONE.grey.soft from palette.
// Replaces all inlined SkeletonBlock / SkeletonLine copies in AmountHero,
// StatBand, and KVGrid.
//
// Display tier — pure props in, JSX out. No useState. No data.
// All visual values from `@ds/tokens/palette`.

import {
  MOTION,
  RADIUS,
  TONE,
} from "@ds/tokens/palette";

// ─── Keyframes injection (idempotent) ─────────────────────────────────────────
//
// A single shared @keyframes rule, injected once per page load. All Skeleton
// instances reference the same animation name so there's only one rule in the
// document regardless of how many Skeleton components are rendered.

const KEYFRAMES_ID = "ds-skeleton-pulse";
const KEYFRAME_NAME = "ds-skeleton-pulse";

function injectKeyframes(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(KEYFRAMES_ID)) return;
  const style = document.createElement("style");
  style.id = KEYFRAMES_ID;
  style.textContent = `
    @keyframes ${KEYFRAME_NAME} {
      0%, 100% { opacity: 0.55; }
      50%       { opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

// ─── Inputs (data props — the model) ─────────────────────────────────────────

export interface SkeletonInput {
  width: number | string;
  height: number | string;
  /** Border radius. Default RADIUS.xs. */
  radius?: number;
  testId?: string;
}

// No Outputs or Actions — Skeleton is a pure display atom.

export type SkeletonProps = SkeletonInput;

// ─── Implementation ───────────────────────────────────────────────────────────

export function Skeleton({
  width,
  height,
  radius = RADIUS.xs,
  testId,
}: SkeletonProps) {
  injectKeyframes();
  return (
    <span
      aria-hidden
      data-testid={testId}
      style={{
        display:      "inline-block",
        width,
        height,
        borderRadius: radius,
        background:   TONE.grey.soft,
        animation:    `${KEYFRAME_NAME} ${MOTION.skeletonPulse}`,
      }}
    />
  );
}
